import { ChatAttachments, ChatCard, ChatLink, ChatMarkdown } from '@wundo/ui';
import {
  MAX_ATTACHMENTS_PER_MESSAGE,
  formattedFileSizes,
  unavailableSubject,
} from '@wundo/shared/attachments.ts';
import { formatFileSize } from '@/utils/messageUtils.ts';
import { AdaptiveCardPreview } from '@/components/admin/notifications/AdaptiveCardPreview.tsx';
import { SlackBlocksPreview } from '@/components/admin/notifications/SlackBlocksPreview.tsx';
import type {
  AttachmentFile,
  AttachmentMessageBody,
  MessageBodyType,
  NotificationMessageBody,
} from '@/types/messages.ts';

/*
 * The new files-list shape and the legacy single-file one, as one list of
 * things that can actually be opened.
 */
function viewableFiles(body: AttachmentMessageBody) {
  return attachedFiles(body)
    .filter((file): file is AttachmentFile & { url: string } => Boolean(file.url))
    .map((file) => ({
      kind: file.mime_type?.startsWith('image/') ? ('image' as const) : ('file' as const),
      name: file.file_name || 'Attachment',
      detail: file.file_size ? formatFileSize(file.file_size) : undefined,
      href: file.url,
    }));
}

function attachedFiles(body: AttachmentMessageBody): AttachmentFile[] {
  if (body.files?.length) {
    return body.files;
  }
  if (body.url) {
    return [
      { url: body.url, mime_type: body.mime_type, file_name: body.file_name ?? '' },
    ];
  }
  return [];
}

/** Why a file the user shared is not here, in the words the channels use. */
function unavailableLabel(file: AttachmentFile): string {
  const count = file.count ?? 1;

  switch (file.unavailable_reason) {
    case 'too_large': {
      const sizes = formattedFileSizes(file.sizes, count);
      return `${unavailableSubject(count, file.file_names)} too large to display${sizes ? ` (${sizes})` : ''}`;
    }
    case 'link_only':
      return `${unavailableSubject(count, file.file_names)} shared as a OneDrive/SharePoint link, not uploaded`;
    case 'too_many':
      return `${unavailableSubject(count, file.file_names, true)} shared but not shown (max ${MAX_ATTACHMENTS_PER_MESSAGE} per message)`;
    default:
      return `${unavailableSubject(count, file.file_names)} could not be read — supported: PDF, images, text`;
  }
}

function Attachment({
  body,
  fromUser,
}: {
  body: AttachmentMessageBody;
  fromUser: boolean;
}) {
  const files = viewableFiles(body);
  const unavailable = (body.files ?? []).filter((file) => file.unavailable_reason);

  return (
    <div className='flex flex-col gap-2'>
      {body.caption ? <ChatMarkdown>{body.caption}</ChatMarkdown> : null}
      <ChatAttachments tone={fromUser ? 'onPrimary' : 'surface'} files={files} />
      {unavailable.map((file, index) => (
        <p key={`unavailable-${index}`} className='text-sm italic'>
          {unavailableLabel(file)}
        </p>
      ))}
    </div>
  );
}

/*
 * The card exactly as the channel drew it, when the message kept it. Buttons
 * stay inert: a url can be a sign-in link minted for the person the
 * notification went to, which is nobody else's to open.
 */
function Notification({ body }: { body: NotificationMessageBody }) {
  if (body.delivered?.channel === 'msteams') {
    return (
      <div className='flex flex-col gap-2'>
        {body.delivered.cards.map((card, index) => (
          <AdaptiveCardPreview key={index} card={card} />
        ))}
      </div>
    );
  }

  if (body.delivered?.channel === 'slack') {
    return <SlackBlocksPreview blocks={body.delivered.blocks} />;
  }

  const buttons = body.button
    ? [{ id: body.button.id, title: body.button.text }]
    : undefined;
  const images = body.images ?? [];

  if (images.length === 0) {
    return (
      <ChatCard
        header={body.title}
        body={body.text}
        footer={[body.status, body.participants].filter(Boolean).join(' · ') || undefined}
        buttons={buttons}
        disabled
      />
    );
  }

  const name = body.title ?? 'Notification';

  return (
    <div className='flex flex-col gap-2'>
      {images.map((src, index) => (
        <img
          key={src}
          src={src}
          alt={images.length > 1 ? `${name} (${index + 1} of ${images.length})` : name}
          className='w-full rounded-md'
        />
      ))}
      {buttons ? <ChatCard buttons={buttons} disabled /> : null}
    </div>
  );
}

export interface MessageBodyProps {
  body: MessageBodyType;
  /** Draws the attachment chips for the brand-coloured bubble rather than a plain one. */
  fromUser: boolean;
  /** Says a card's button as the user. Absent and every button is inert. */
  onButtonClick?: (title: string) => void;
  /** Whether there is still anything to answer here. */
  answered?: boolean;
}

/**
 * What a message says, whatever shape it arrived in.
 *
 * Every kind Wundo can send over any channel: plain text, a template, a
 * button, a link, an Adaptive-Card-style card, files, and the pause the
 * orchestrator inserts between two turns.
 */
export function MessageBody({
  body,
  fromUser,
  onButtonClick,
  answered = true,
}: MessageBodyProps) {
  switch (body.type) {
    case 'text':
    case 'button':
      return <ChatMarkdown>{body.text}</ChatMarkdown>;

    case 'template':
      return <ChatMarkdown>{body.template.body}</ChatMarkdown>;

    case 'pause':
      return <p className='text-sm'>{`Waited ${body.duration} milliseconds`}</p>;

    case 'link':
      return <ChatLink text={body.text} linkText={body.link_text} url={body.url} />;

    case 'attachment':
      return <Attachment body={body} fromUser={fromUser} />;

    case 'card':
      return (
        <ChatCard
          header={body.card.header}
          body={body.card.body}
          footer={body.card.footer}
          sections={body.card.sections}
          buttons={body.card.buttons}
          onAction={onButtonClick}
          disabled={answered}
        />
      );

    case 'notification':
      return <Notification body={body} />;
  }
}
